import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Stack,
  Button,
  Alert,
  Collapse
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DocumentRow from "./Documentrow";
import DialogModal from "../DialogModel";
import { validateFile, ACCEPTED_FILE_TYPES_LABEL, MAX_FILE_SIZE_MB, ACCEPTED_FILE_EXTENSIONS } from "../../../utils/formHelper";
import "./Wagedocumentsmodal.scss";

const WageDocumentsModal = ({
  open,
  onClose,
  onDone,
  files,
  disabled = false,
  onAddFile,
  onLabelChange,
  onDeleteFile,
  onViewFile,
  onDownloadFile
}) => {
  const fileInputRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const pendingDeleteFile = pendingDeleteId ? files.find((file) => file.id === pendingDeleteId) || null : null;
  const errorFileNameMatch = errorMessage.match(/^"[^"]+"/);
  const errorFileName = errorFileNameMatch ? errorFileNameMatch[0] : "";
  const errorRest = errorFileNameMatch ? errorMessage.slice(errorFileName.length) : errorMessage;
  const handleBrowseClick = () => { fileInputRef.current?.click();};

  const processIncomingFile = (file) => {
    if (!file) return;

    const { valid, error } = validateFile(file);

    if (!valid) {
      setErrorMessage(error);
      return;
    }

    setErrorMessage("");
    onAddFile(file);
  };

  const handleFileInputChange = (event) => {
    const [file] = event.target.files;
    processIncomingFile(file);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);

    if (event.dataTransfer.files.length > 1) {
      setErrorMessage("Please drop one file at a time.");
      return;
    }

    const [file] = event.dataTransfer.files;
    processIncomingFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  const handleRequestDelete = (fileId) => {
    setPendingDeleteId(fileId);
  };

  const handleCancelDelete = () => {
    setPendingDeleteId(null);
  };

  const handleConfirmDelete = () => {
    onDeleteFile(pendingDeleteId);
    setPendingDeleteId(null);
  };

  return (
    <>
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth className="wage-documents-modal">
        <DialogTitle className="wage-documents-modal_header">
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="subtitle1" className="wage-documents-modal_title">
                Wage Determination Documents
              </Typography>
              <Typography variant="caption" color="text.secondary" className="wage-documents-modal_subtitle">
                {disabled
                  ? "View or download the attached documents below."
                  : `Attach supporting files. ${ACCEPTED_FILE_TYPES_LABEL} only, ${MAX_FILE_SIZE_MB} MB max each.`}
              </Typography>
            </Box>
  
            <IconButton size="small" onClick={onClose} className="wage-documents-modal_close-btn" aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
  
          <Typography variant="caption" color="text.secondary" className="wage-documents-modal_counter">
            {`${files.length} file${files.length === 1 ? "" : "s"} attached`}
          </Typography>
        </DialogTitle>
  
        <DialogContent className="wage-documents-modal_content">
          {!disabled && (
            <Box
              className={`wage-documents-modal_dropzone ${isDragActive ? "wage-documents-modal_dropzone-active" : ""}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <Box className="wage-documents-modal_dropzone-icon-badge">
                <CloudUploadOutlinedIcon className="wage-documents-modal_dropzone-icon" fontSize="small" />
              </Box>

              <Typography variant="body2" className="wage-documents-modal_dropzone-text">
                Drag file here or{" "}
                <Box component="span" className="wage-documents-modal_browse-link" onClick={handleBrowseClick}>
                  browse
                </Box>
              </Typography>

              <Typography variant="caption" color="text.secondary" className="wage-documents-modal_dropzone-caption">
                {ACCEPTED_FILE_TYPES_LABEL} format only &bull; {MAX_FILE_SIZE_MB} MB max &bull; one file at a time
              </Typography>

              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept={ACCEPTED_FILE_EXTENSIONS.join(",")}
                onChange={handleFileInputChange}
              />
            </Box>
          )}

          {!disabled && (
            <Collapse in={Boolean(errorMessage)}>
              <Alert severity="error" icon={<ErrorOutlineIcon fontSize="small" />} className="wage-documents-modal_error-alert">
                <Typography component="span" variant="body2" className="wage-documents-modal_error-lead">
                  {errorFileName}
                </Typography>
                {errorRest}
              </Alert>
            </Collapse>
          )}
  
          <Box className="wage-documents-modal_file-list">
            {files.length === 0 ? (
              <Typography variant="caption" color="text.secondary" className="wage-documents-modal_empty-text">
                {disabled ? "No documents have been attached." : "No files yet. Upload PDF, TXT, DOC, PNG or JPEG documents."}
              </Typography>
            ) : (
              <Stack spacing={0}>
                {files.map((file) => (
                  <DocumentRow
                    key={file.id}
                    file={file}
                    disabled={disabled}
                    onLabelChange={onLabelChange}
                    onView={onViewFile}
                    onDownload={onDownloadFile}
                    onDelete={handleRequestDelete}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </DialogContent>
  
        <DialogActions className="wage-documents-modal_footer">
          <Stack direction="row" spacing={0.75} alignItems="center" className="wage-documents-modal_visibility-indicator">
            <GppGoodOutlinedIcon fontSize="small" className="wage-documents-modal_visibility-icon" />
            <Typography variant="caption" color="text.secondary">
              Visible to EPC and Subcontractors in the Project Profile
            </Typography>
          </Stack>
  
          <Button variant="contained" onClick={onDone} className="wage-documents-modal_done-btn">
            Done
          </Button>
        </DialogActions>
        </Dialog>

      <DialogModal
        show={Boolean(pendingDeleteFile)}
        title="Remove document"
        message={`Are you sure you want to remove "${pendingDeleteFile?.name}"? This can't be undone.`}
        confirmBtnText="Remove"
        cancelBtnText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};

export default WageDocumentsModal;